// The one interactive floor-plan canvas for the whole dashboard: pan
// (drag / one-finger), zoom (wheel / pinch / buttons), fit-to-plan, and a
// zone picker. Renders the shared SvgTableFigure so it always matches the
// editor. Generic over what a table "is": pass a tableInfo map plus optional
// click / selection / accent / overlay so both the reservations plan and the
// table-ordering live floor use the exact same canvas.
import { CHAIR_GAP, CHAIR_THICK } from '@/features/reservation-setup/utils/tableChairs';
import {
  SvgTableFigure,
  type TableElement,
} from '@/features/reservation-setup/utils/SvgTableFigure';
import { useT } from '@/i18n';
import type { FloorPlanElement } from '@restaurant/api-client';
import { f7 } from 'framework7-react';
import { useEffect, useMemo, useRef, useState } from 'react';

const ADV_PAD = 56; // viewport padding around the saved-plan bounding box
// so the outermost tables/chairs never touch the canvas edge (was 24 - the
// plan visibly clipped the edge tables on a narrow phone)
const MIN_SCALE = 0.2;
const MAX_SCALE = 6;
const clampScale = (s: number) => Math.max(MIN_SCALE, Math.min(MAX_SCALE, s));

type LabelElement = Extract<FloorPlanElement, { kind: 'label' }>;
type WallElement = Extract<FloorPlanElement, { kind: 'wall' }>;

export interface FloorTableInfo {
  label: string;
  seats: number;
  zoneId: string | null;
}

export function InteractiveFloorPlan({
  elements,
  tableInfo,
  zones,
  canvas,
  activeZoneId,
  selectedTableId = null,
  topInset = 0,
  bottomInset = 0,
  onTableClick,
  accentFor,
  renderOverlay,
}: {
  elements: FloorPlanElement[];
  tableInfo: Map<string, FloorTableInfo>;
  zones: { id: string; label: string; sortOrder: number }[];
  canvas: { width: number; height: number };
  /** Controlled zone filter. When provided (incl. `null` = "No zone"), the
   *  in-canvas zone picker is hidden and the parent owns zone selection
   *  (e.g. the Tables tab's zone tab bar). Omit it for the uncontrolled
   *  in-canvas picker (reservations plan). */
  activeZoneId?: string | null;
  selectedTableId?: string | null;
  /** Regions (px) the canvas runs *under* - e.g. a navbar+subnavbar at the top
   *  and a bottom tab bar. The canvas fills the whole area (so dragged content
   *  slides under the bars instead of being clipped at a hard seam), but the
   *  fit reserves these so the plan rests in the clear band between them. */
  topInset?: number;
  bottomInset?: number;
  onTableClick?: (tableId: string) => void;
  accentFor?: (tableId: string) => string | undefined;
  renderOverlay?: (tableId: string, el: TableElement) => React.ReactNode;
}) {
  const t = useT();
  const rs = t.reservationSetup;
  const controlled = activeZoneId !== undefined;
  const allTableEls = useMemo(
    () => elements.filter((e): e is TableElement => e.kind === 'table'),
    [elements],
  );
  const labelEls = useMemo(
    () => elements.filter((e): e is LabelElement => e.kind === 'label'),
    [elements],
  );
  const wallEls = useMemo(
    () => elements.filter((e): e is WallElement => e.kind === 'wall'),
    [elements],
  );

  // Zone options = zones that actually have placed tables in this plan.
  const zoneOptions = useMemo(() => {
    const placed = new Set<string | null>();
    for (const el of allTableEls) placed.add(tableInfo.get(el.tableId)?.zoneId ?? null);
    const out: { id: string | null; label: string }[] = [];
    for (const z of zones.slice().sort((a, b) => a.sortOrder - b.sortOrder)) {
      if (placed.has(z.id)) out.push({ id: z.id, label: z.label });
    }
    if (placed.has(null)) out.push({ id: null, label: rs.noZoneLabel });
    return out;
  }, [allTableEls, tableInfo, zones, rs.noZoneLabel]);

  // Uncontrolled fallback (reservations in-canvas picker). When `controlled`,
  // the parent owns the zone and this state/effect/picker is unused.
  const [internalZoneId, setInternalZoneId] = useState<string | null | undefined>(undefined);
  useEffect(() => {
    if (controlled) return;
    if (internalZoneId === undefined && zoneOptions.length > 0) {
      setInternalZoneId(zoneOptions[0].id);
    } else if (
      internalZoneId !== undefined &&
      zoneOptions.length > 0 &&
      !zoneOptions.some((z) => z.id === internalZoneId)
    ) {
      setInternalZoneId(zoneOptions[0].id);
    }
  }, [controlled, zoneOptions, internalZoneId]);

  const effectiveZoneId = controlled ? activeZoneId : internalZoneId;
  const activeLabel = zoneOptions.find((z) => z.id === effectiveZoneId)?.label ?? null;

  const tableEls = useMemo(
    () =>
      effectiveZoneId === undefined
        ? []
        : allTableEls.filter(
            (el) => (tableInfo.get(el.tableId)?.zoneId ?? null) === effectiveZoneId,
          ),
    [allTableEls, tableInfo, effectiveZoneId],
  );

  // Content box: bbox of visible elements + chair gutter + pad. This is the
  // region the fit makes fully visible (independent of viewport size).
  const cb = useMemo(() => {
    let minX = Infinity;
    let minY = Infinity;
    let maxX = -Infinity;
    let maxY = -Infinity;
    for (const el of tableEls) {
      minX = Math.min(minX, el.x - CHAIR_THICK - CHAIR_GAP);
      minY = Math.min(minY, el.y - CHAIR_THICK - CHAIR_GAP);
      maxX = Math.max(maxX, el.x + el.width + CHAIR_THICK + CHAIR_GAP);
      maxY = Math.max(maxY, el.y + el.height + CHAIR_THICK + CHAIR_GAP);
    }
    for (const w of wallEls) {
      minX = Math.min(minX, Math.min(w.x1, w.x2));
      minY = Math.min(minY, Math.min(w.y1, w.y2));
      maxX = Math.max(maxX, Math.max(w.x1, w.x2));
      maxY = Math.max(maxY, Math.max(w.y1, w.y2));
    }
    if (!Number.isFinite(minX)) {
      minX = 0;
      minY = 0;
      maxX = canvas.width;
      maxY = canvas.height;
    }
    return {
      x: minX - ADV_PAD,
      y: minY - ADV_PAD,
      w: maxX - minX + ADV_PAD * 2,
      h: maxY - minY + ADV_PAD * 2,
    };
  }, [tableEls, wallEls, canvas]);

  const svgRef = useRef<SVGSVGElement>(null);
  // The canvas runs full-bleed under the bars; we track its rendered size so
  // the fit can reserve `topInset`/`bottomInset` px there (keeping the plan in
  // the clear band while letting dragged content slide under the bars). Only
  // consulted when there IS an inset - otherwise `home` stays `cb` (pure
  // geometry, viewport-independent) so other callers' fit & resize behaviour
  // is unchanged.
  const insets = topInset + bottomInset;
  const [rect, setRect] = useState<{ width: number; height: number } | null>(null);
  useEffect(() => {
    const el = svgRef.current;
    if (!el || insets <= 0) return;
    const measure = () => {
      const r = el.getBoundingClientRect();
      setRect({ width: r.width, height: r.height });
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [insets]);

  // Auto-fit "home" viewBox. With insets, expand the box to viewport aspect and
  // shift it so the content sits centered in the CLEAR band between the bars;
  // the reserved strips become letterbox the canvas paints into as you pan.
  // Without insets, the meet-fit centres `cb` as before.
  const home = useMemo(() => {
    const band = rect ? rect.height - topInset - bottomInset : 0;
    if (insets <= 0 || !rect || rect.width <= 0 || band <= 0) {
      return cb;
    }
    const scale = Math.min(rect.width / cb.w, band / cb.h);
    if (!Number.isFinite(scale) || scale <= 0) return cb;
    const w = rect.width / scale;
    const h = rect.height / scale;
    return {
      x: cb.x + cb.w / 2 - w / 2,
      y: cb.y + cb.h / 2 - (topInset + rect.height - bottomInset) / (2 * scale),
      w,
      h,
    };
  }, [cb, topInset, bottomInset, insets, rect]);

  const [vb, setVb] = useState(home);
  useEffect(() => setVb(home), [home]);

  const dragRef = useRef<{
    startClient: { x: number; y: number };
    startVb: typeof vb;
    moved: boolean;
  } | null>(null);
  const pinchRef = useRef<{ dist: number } | null>(null);

  const zoomBy = (factor: number, clientX: number, clientY: number) => {
    setVb((prev) => {
      const rect = svgRef.current?.getBoundingClientRect();
      if (!rect) return prev;
      const scale = Math.min(rect.width / prev.w, rect.height / prev.h);
      const next = clampScale(scale * factor) / scale;
      const newW = prev.w / next;
      const newH = prev.h / next;
      const px = (clientX - rect.left) / rect.width;
      const py = (clientY - rect.top) / rect.height;
      const svgX = prev.x + px * prev.w;
      const svgY = prev.y + py * prev.h;
      return { x: svgX - px * newW, y: svgY - py * newH, w: newW, h: newH };
    });
  };

  return (
    <>
      <svg
        ref={svgRef}
        className="rsv-plan-svg"
        viewBox={`${vb.x} ${vb.y} ${vb.w} ${vb.h}`}
        preserveAspectRatio="xMidYMid meet"
        role="img"
        aria-label={rs.floorPlanAriaLabel}
        style={{ touchAction: 'none', cursor: dragRef.current ? 'grabbing' : 'grab' }}
        onMouseDown={(e) => {
          if (e.button !== 0) return;
          dragRef.current = {
            startClient: { x: e.clientX, y: e.clientY },
            startVb: vb,
            moved: false,
          };
        }}
        onMouseMove={(e) => {
          if (!dragRef.current) return;
          const dx = e.clientX - dragRef.current.startClient.x;
          const dy = e.clientY - dragRef.current.startClient.y;
          if (Math.abs(dx) + Math.abs(dy) > 3) dragRef.current.moved = true;
          const start = dragRef.current.startVb;
          const rect = svgRef.current!.getBoundingClientRect();
          const scale = Math.min(rect.width / start.w, rect.height / start.h);
          setVb({ ...start, x: start.x - dx / scale, y: start.y - dy / scale });
        }}
        onMouseUp={() => {
          dragRef.current = null;
        }}
        onMouseLeave={() => {
          dragRef.current = null;
        }}
        onWheel={(e) => {
          e.preventDefault();
          zoomBy(e.deltaY > 0 ? 1 / 1.12 : 1.12, e.clientX, e.clientY);
        }}
        onTouchStart={(e) => {
          if (e.touches.length === 1) {
            const t = e.touches[0];
            dragRef.current = {
              startClient: { x: t.clientX, y: t.clientY },
              startVb: vb,
              moved: false,
            };
          } else if (e.touches.length === 2) {
            dragRef.current = null;
            const [a, b] = [e.touches[0], e.touches[1]];
            pinchRef.current = {
              dist: Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY),
            };
          }
        }}
        onTouchMove={(e) => {
          if (e.touches.length === 1 && dragRef.current) {
            const t = e.touches[0];
            const dx = t.clientX - dragRef.current.startClient.x;
            const dy = t.clientY - dragRef.current.startClient.y;
            if (Math.abs(dx) + Math.abs(dy) > 3) dragRef.current.moved = true;
            const start = dragRef.current.startVb;
            const rect = svgRef.current!.getBoundingClientRect();
            const scale = Math.min(rect.width / start.w, rect.height / start.h);
            setVb({ ...start, x: start.x - dx / scale, y: start.y - dy / scale });
          } else if (e.touches.length === 2 && pinchRef.current) {
            const [a, b] = [e.touches[0], e.touches[1]];
            const dist = Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY);
            const factor = dist / pinchRef.current.dist;
            const cx = (a.clientX + b.clientX) / 2;
            const cy = (a.clientY + b.clientY) / 2;
            zoomBy(factor, cx, cy);
            pinchRef.current = { dist };
          }
        }}
        onTouchEnd={(e) => {
          if (e.touches.length === 0) {
            dragRef.current = null;
            pinchRef.current = null;
          } else if (e.touches.length === 1) {
            pinchRef.current = null;
            const t = e.touches[0];
            dragRef.current = {
              startClient: { x: t.clientX, y: t.clientY },
              startVb: vb,
              moved: false,
            };
          }
        }}
      >
        {wallEls.map((w, i) => (
          <line
            key={`wall-${i}`}
            x1={w.x1}
            y1={w.y1}
            x2={w.x2}
            y2={w.y2}
            stroke="#475569"
            strokeWidth={w.thickness}
            strokeLinecap="round"
          />
        ))}
        {labelEls.map((l, i) => (
          <text
            key={`label-${i}`}
            x={l.x}
            y={l.y}
            fontSize={14}
            fontWeight={700}
            fill="#64748b"
            fontFamily="system-ui, -apple-system, sans-serif"
            dominantBaseline="hanging"
          >
            {l.text}
          </text>
        ))}
        {tableEls.map((el) => {
          const info = tableInfo.get(el.tableId);
          return (
            <SvgTableFigure
              key={el.tableId}
              el={el}
              label={info?.label ?? el.tableId.slice(-4)}
              seats={info?.seats ?? 0}
              accentStroke={accentFor?.(el.tableId)}
              selected={selectedTableId === el.tableId}
              onClick={
                onTableClick
                  ? () => {
                      // Suppress the click that ends a pan gesture.
                      if (dragRef.current?.moved) return;
                      onTableClick(el.tableId);
                    }
                  : undefined
              }
            >
              {renderOverlay?.(el.tableId, el)}
            </SvgTableFigure>
          );
        })}
      </svg>

      {!controlled && zoneOptions.length > 1 && (
        <button
          type="button"
          className="floor-zone-chip rsv-plan-zone-picker"
          onClick={() => {
            f7.actions
              .create({
                buttons: [
                  zoneOptions.map((z) => ({
                    text: z.label,
                    bold: z.id === internalZoneId,
                    onClick: () => setInternalZoneId(z.id),
                  })),
                  [{ text: t.cancel, color: 'red' }],
                ],
              })
              .open();
          }}
        >
          <span>{activeLabel ?? rs.noZoneLabel}</span>
          <i className="f7-icons">chevron_down</i>
        </button>
      )}

      <div className="canvas-zoom">
        <div className="canvas-zoom-group">
          <button
            type="button"
            className="canvas-zoom-btn"
            aria-label={rs.zoomIn}
            onClick={(e) => {
              const rect = svgRef.current!.getBoundingClientRect();
              zoomBy(1.25, rect.left + rect.width / 2, rect.top + rect.height / 2);
              e.currentTarget.blur();
            }}
          >
            +
          </button>
          <button
            type="button"
            className="canvas-zoom-btn"
            aria-label={rs.zoomOut}
            onClick={(e) => {
              const rect = svgRef.current!.getBoundingClientRect();
              zoomBy(1 / 1.25, rect.left + rect.width / 2, rect.top + rect.height / 2);
              e.currentTarget.blur();
            }}
          >
            −
          </button>
        </div>
        <button
          type="button"
          className="canvas-zoom-btn canvas-zoom-fit"
          aria-label={rs.fitToPlan}
          onClick={() => setVb(home)}
        >
          ⤢
        </button>
      </div>
    </>
  );
}
